'use client';
import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import type { CSSProperties, ReactElement, ReactNode, Ref } from 'react';
import { motion, type MotionProps } from 'framer-motion';
import { IS_SEQ_LOG_ENABLED, usePersistentDebugLogger } from '@/hooks/telemetry';
import type { CcontainerProps, CcontainerRootAttributes } from './Ccontainer.types';
import type { ContainerInteractionHandlers } from './useContainerPressInteraction';
const INHERITED_BORDER_RADIUS = 'inherit';
const CONTENT_LAYER_Z_INDEX = 1;
const GRADIENT_BORDER_LAYER_NAME = 'gradientBorder';
const LOADING_BORDER_LAYER_NAME = 'loadingBorder';
const CCONTAINER_ROOT_COMPONENT_NAME = 'CcontainerRoot';
const CCONTAINER_STYLE_CONFLICT_INSTANCE_ID = 'ccontainer-style-conflict--2026-07-01';
const CCONTAINER_STYLE_CONFLICT_FEATURE = 'ccontainer-style-conflict';
const CCONTAINER_STYLE_CONFLICT_EVENT = 'container-style-background-conflict';
const BACKGROUND_SHORTHAND_KEY = 'background';
const NO_STYLE_VALUE = null;
const BACKGROUND_LONGHAND_KEYS = [
    'backgroundAttachment',
    'backgroundBlendMode',
    'backgroundClip',
    'backgroundColor',
    'backgroundImage',
    'backgroundOrigin',
    'backgroundPosition',
    'backgroundPositionX',
    'backgroundPositionY',
    'backgroundRepeat',
    'backgroundSize',
] as const;
enum ContainerRootRenderMode {
    MotionRoot = 'motion-root',
    StaticRoot = 'static-root'
}
const GLOW_LAYER_STYLE: CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: INHERITED_BORDER_RADIUS,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
};
interface CcontainerRootProps {
    children?: ReactNode;
    className?: string;
    /** Structural `data-*` markers stamped on whichever root element is rendered. */
    rootAttributes?: CcontainerRootAttributes;
    containerStyle: CSSProperties;
    preserveStyle?: CcontainerProps['preserveStyle'];
    contentWrapperStyle: CSSProperties;
    gradientBorderStyle?: CSSProperties;
    loadingBorderStyle?: CSSProperties;
    glowLayer?: ReactNode;
    hasGlowLayer: boolean;
    useGradientBorder: boolean;
    isLoadingActive: boolean;
    onClick?: CcontainerProps['onClick'];
    interactionHandlers: ContainerInteractionHandlers;
    rootMotion?: CcontainerProps['rootMotion'];
    rootRef?: Ref<HTMLDivElement>;
    useClickMotion: boolean;
    clickMotionProps: MotionProps;
    effectiveAnimate?: MotionProps['animate'];
    effectiveTransition?: MotionProps['transition'];
}
type BackgroundLonghandKey = (typeof BACKGROUND_LONGHAND_KEYS)[number];
interface ContainerStyleConflictTelemetryInput {
    className?: string;
    containerStyle: CSSProperties;
    preserveStyle?: CcontainerProps['preserveStyle'];
    rootMotion?: CcontainerProps['rootMotion'];
    shouldUseMotionRoot: boolean;
    useClickMotion: boolean;
}
interface ContainerStyleConflictSnapshotInput {
    className?: string;
    containerStyle: CSSProperties;
    hasPreserveStyle: boolean;
    hasRootMotion: boolean;
    shouldUseMotionRoot: boolean;
    useClickMotion: boolean;
}
interface ContainerStyleConflictSnapshot {
    hasConflict: boolean;
    payload: Record<string, unknown>;
    signature: string;
}
const EMPTY_CONFLICT_SIGNATURE = '';
const NO_CONTAINER_STYLE_CONFLICT: ContainerStyleConflictSnapshot = {
    hasConflict: false,
    payload: {},
    signature: EMPTY_CONFLICT_SIGNATURE,
};
export const CcontainerRoot = memo(function CcontainerRoot({ children, className, rootAttributes, containerStyle, preserveStyle, contentWrapperStyle, gradientBorderStyle, loadingBorderStyle, glowLayer, hasGlowLayer, useGradientBorder, isLoadingActive, onClick, interactionHandlers, rootMotion, rootRef, useClickMotion, clickMotionProps, effectiveAnimate, effectiveTransition, }: CcontainerRootProps): ReactElement {
    const shouldUseMotionRoot = Boolean(rootMotion || useClickMotion);
    useContainerStyleConflictTelemetry({
        className,
        containerStyle,
        preserveStyle,
        rootMotion,
        shouldUseMotionRoot,
        useClickMotion,
    });
    const rootChildren = renderRootChildren({
        children,
        contentWrapperStyle,
        gradientBorderStyle,
        loadingBorderStyle,
        glowLayer,
        hasGlowLayer,
        useGradientBorder,
        isLoadingActive,
    });
    if (shouldUseMotionRoot) {
        return (<>
        {renderMotionRoot({
                className,
                rootAttributes,
                containerStyle,
                preserveStyle,
                onClick,
                interactionHandlers,
                rootMotion,
                rootRef,
                clickMotionProps,
                effectiveAnimate,
                effectiveTransition,
                rootChildren,
            })}
      </>);
    }
    return (<>
      {renderStaticRoot({
            className,
            rootAttributes,
            containerStyle,
            onClick,
            interactionHandlers,
            rootRef,
            rootChildren,
        })}
    </>);
});
/**
 * Runtime companion to the static `no-conflicting-style-shorthand` lint rule: it
 * catches `background` + background-longhand collisions that only exist once the
 * preset, caller `style` and gradient layers are merged at runtime. The snapshot
 * is the expensive part (key scan + payload stringify), so it is memoized on the
 * merged style identity and skipped entirely when the Seq transport is off —
 * without a transport `logger.logMarker` is a no-op, so there is nothing to build.
 */
function useContainerStyleConflictTelemetry(input: ContainerStyleConflictTelemetryInput): void {
    const { className, containerStyle, preserveStyle, rootMotion, shouldUseMotionRoot, useClickMotion } = input;
    const logger = usePersistentDebugLogger({
        component: CCONTAINER_ROOT_COMPONENT_NAME,
        instanceId: CCONTAINER_STYLE_CONFLICT_INSTANCE_ID,
        metadata: {
            feature: CCONTAINER_STYLE_CONFLICT_FEATURE,
        },
    });
    const previousSignatureRef = useRef<string | null>(null);
    const hasPreserveStyle = preserveStyle != null;
    const hasRootMotion = rootMotion != null;
    const snapshot = useMemo<ContainerStyleConflictSnapshot>(() => {
        if (!IS_SEQ_LOG_ENABLED) {
            return NO_CONTAINER_STYLE_CONFLICT;
        }
        return buildContainerStyleConflictSnapshot({
            className,
            containerStyle,
            hasPreserveStyle,
            hasRootMotion,
            shouldUseMotionRoot,
            useClickMotion,
        });
    }, [className, containerStyle, hasPreserveStyle, hasRootMotion, shouldUseMotionRoot, useClickMotion]);
    useLayoutEffect(() => {
        if (!snapshot.hasConflict) {
            previousSignatureRef.current = null;
            return;
        }
        if (previousSignatureRef.current === snapshot.signature) {
            return;
        }
        logger.logMarker({
            name: CCONTAINER_STYLE_CONFLICT_EVENT,
            payload: snapshot.payload,
        });
        previousSignatureRef.current = snapshot.signature;
    }, [logger, snapshot]);
}
function buildContainerStyleConflictSnapshot(input: ContainerStyleConflictSnapshotInput): ContainerStyleConflictSnapshot {
    const background = input.containerStyle[BACKGROUND_SHORTHAND_KEY];
    const hasBackgroundShorthand = isPresentStyleValue(background);
    if (!hasBackgroundShorthand) {
        return NO_CONTAINER_STYLE_CONFLICT;
    }
    const presentLonghandKeys = BACKGROUND_LONGHAND_KEYS.filter((key) => isPresentStyleValue(input.containerStyle[key]));
    if (presentLonghandKeys.length === 0) {
        return NO_CONTAINER_STYLE_CONFLICT;
    }
    const payload = {
        className: input.className ?? NO_STYLE_VALUE,
        conflictLonghandKeys: presentLonghandKeys,
        hasBackgroundShorthand,
        hasPreserveStyle: input.hasPreserveStyle,
        hasRootMotion: input.hasRootMotion,
        renderMode: input.shouldUseMotionRoot ? ContainerRootRenderMode.MotionRoot : ContainerRootRenderMode.StaticRoot,
        shouldUseMotionRoot: input.shouldUseMotionRoot,
        styleBackground: stringifyStyleValue(background),
        styleLonghands: buildBackgroundLonghandPayload({
            containerStyle: input.containerStyle,
            presentLonghandKeys,
        }),
        styleKeys: Object.keys(input.containerStyle),
        useClickMotion: input.useClickMotion,
    };
    return {
        hasConflict: true,
        payload,
        signature: JSON.stringify(payload),
    };
}
function buildBackgroundLonghandPayload(input: {
    containerStyle: CSSProperties;
    presentLonghandKeys: readonly BackgroundLonghandKey[];
}): Record<string, string | null> {
    return input.presentLonghandKeys.reduce<Record<string, string | null>>((accumulator, key) => {
        accumulator[key] = stringifyStyleValue(input.containerStyle[key]);
        return accumulator;
    }, {});
}
function isPresentStyleValue(value: unknown): boolean {
    return value != null && value !== '';
}
function stringifyStyleValue(value: unknown): string | null {
    if (value == null) {
        return NO_STYLE_VALUE;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
}
function renderRootChildren(input: {
    children?: ReactNode;
    contentWrapperStyle: CSSProperties;
    gradientBorderStyle?: CSSProperties;
    loadingBorderStyle?: CSSProperties;
    glowLayer?: ReactNode;
    hasGlowLayer: boolean;
    useGradientBorder: boolean;
    isLoadingActive: boolean;
}): ReactNode {
    return (<>
      {renderGlowLayer({
            hasGlowLayer: input.hasGlowLayer,
            glowLayer: input.glowLayer,
        })}
      {renderBorderLayer({
            layerName: GRADIENT_BORDER_LAYER_NAME,
            style: input.gradientBorderStyle,
        })}
      {renderBorderLayer({
            layerName: LOADING_BORDER_LAYER_NAME,
            style: input.loadingBorderStyle,
        })}
      <div style={buildContentLayerStyle({
            contentWrapperStyle: input.contentWrapperStyle,
            useGradientBorder: input.useGradientBorder,
            isLoadingActive: input.isLoadingActive,
            hasGlowLayer: input.hasGlowLayer,
        })}>
        {input.children ?? null}
      </div>
    </>);
}
function renderGlowLayer(input: {
    hasGlowLayer: boolean;
    glowLayer?: ReactNode;
}): ReactNode {
    if (!input.hasGlowLayer) {
        return null;
    }
    return (<div aria-hidden="true" style={GLOW_LAYER_STYLE}>
      {input.glowLayer}
    </div>);
}
function renderBorderLayer(input: {
    layerName: typeof GRADIENT_BORDER_LAYER_NAME | typeof LOADING_BORDER_LAYER_NAME;
    style?: CSSProperties;
}): ReactNode {
    if (!input.style) {
        return null;
    }
    if (input.layerName === GRADIENT_BORDER_LAYER_NAME) {
        return <div style={input.style} aria-hidden="true"/>;
    }
    return <div style={input.style} aria-hidden="true"/>;
}
function buildContentLayerStyle(input: {
    contentWrapperStyle: CSSProperties;
    useGradientBorder: boolean;
    isLoadingActive: boolean;
    hasGlowLayer: boolean;
}): CSSProperties {
    const layeredContent = isLayeredContent(input);
    return {
        position: layeredContent ? 'relative' : undefined,
        zIndex: layeredContent ? CONTENT_LAYER_Z_INDEX : undefined,
        ...input.contentWrapperStyle,
    };
}
function isLayeredContent(input: {
    useGradientBorder: boolean;
    isLoadingActive: boolean;
    hasGlowLayer: boolean;
}): boolean {
    return input.useGradientBorder || input.isLoadingActive || input.hasGlowLayer;
}
function renderMotionRoot(input: {
    className?: string;
    rootAttributes?: CcontainerRootAttributes;
    containerStyle: CSSProperties;
    preserveStyle?: CcontainerProps['preserveStyle'];
    onClick?: CcontainerProps['onClick'];
    interactionHandlers: ContainerInteractionHandlers;
    rootMotion?: CcontainerProps['rootMotion'];
    rootRef?: Ref<HTMLDivElement>;
    clickMotionProps: MotionProps;
    effectiveAnimate?: MotionProps['animate'];
    effectiveTransition?: MotionProps['transition'];
    rootChildren: ReactNode;
}): ReactNode {
    return (<motion.div {...input.clickMotionProps} {...input.rootAttributes} className={input.className} style={{ ...input.containerStyle, ...input.preserveStyle }} onClick={input.onClick} onTouchStart={input.interactionHandlers.onTouchStart} onTouchEnd={input.interactionHandlers.onTouchEnd} onTouchCancel={input.interactionHandlers.onTouchCancel} onMouseEnter={input.interactionHandlers.onMouseEnter} onMouseLeave={input.interactionHandlers.onMouseLeave} onMouseDown={input.interactionHandlers.onMouseDown} onMouseUp={input.interactionHandlers.onMouseUp} initial={input.rootMotion?.initial} animate={input.effectiveAnimate} exit={input.rootMotion?.exit} transition={input.effectiveTransition} layout={input.rootMotion?.layout} ref={input.rootRef}>
      {input.rootChildren}
    </motion.div>);
}
function renderStaticRoot(input: {
    className?: string;
    rootAttributes?: CcontainerRootAttributes;
    containerStyle: CSSProperties;
    onClick?: CcontainerProps['onClick'];
    interactionHandlers: ContainerInteractionHandlers;
    rootRef?: Ref<HTMLDivElement>;
    rootChildren: ReactNode;
}): ReactNode {
    return (<div {...input.rootAttributes} className={input.className} style={input.containerStyle} onClick={input.onClick} onTouchStart={input.interactionHandlers.onTouchStart} onTouchEnd={input.interactionHandlers.onTouchEnd} onTouchCancel={input.interactionHandlers.onTouchCancel} onMouseEnter={input.interactionHandlers.onMouseEnter} onMouseLeave={input.interactionHandlers.onMouseLeave} onMouseDown={input.interactionHandlers.onMouseDown} onMouseUp={input.interactionHandlers.onMouseUp} ref={input.rootRef}>
      {input.rootChildren}
    </div>);
}
